import React, { useState } from 'react';
import { Droplets } from 'lucide-react';
import { waterBalanceService } from '../../services/waterBalanceService';
import { recommendationText, statusNotes, emitterRateMmH } from '../../utils/waterBalanceView';

const MODE_LABEL = {
    tr: { OFF: 'Kapalı', ADVISE: 'Öneri', AUTO: 'Otomatik' },
    en: { OFF: 'Off', ADVISE: 'Advise', AUTO: 'Auto' },
};
const box = { border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', marginTop: 10, background: '#f8fafc', fontSize: 12 };
const input = { width: 70, padding: '3px 6px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 };
const btn = (bg, fg = '#fff') => ({ background: bg, color: fg, border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' });
const label = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, margin: '4px 0' };

// Weather sources a field can use: FieldClimate stations and insigh.io meteo stations.
const weatherSensors = (sensors) => sensors.filter(s => s.fieldclimateStationId || /meteo/i.test(s.name || ''));

export default function WaterBalanceBox({ balance, field, sensors = [], isAdmin, language, onChanged }) {
    const lang = language === 'en' ? 'en' : 'tr';
    const en = lang === 'en';
    const [amount, setAmount] = useState('');
    const [unit, setUnit] = useState('mm');
    const [showSettings, setShowSettings] = useState(false);
    const [form, setForm] = useState(null);
    const [emitter, setEmitter] = useState({ litresPerHour: '', emitters: '' });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    if (!balance) return null;
    const s = balance.settings || {};

    const run = async (fn) => {
        setBusy(true); setError(null);
        try { await fn(); onChanged && onChanged(); } catch (e) { setError(e.message); } finally { setBusy(false); }
    };
    const submitApplied = () => run(async () => {
        const v = Number(amount);
        if (!(v > 0)) throw new Error(en ? 'Enter an amount' : 'Miktar girin');
        await waterBalanceService.recordApplied(field.id, unit === 'mm' ? { mm: v } : { minutes: v });
        setAmount('');
    });
    const openSettings = () => {
        setForm({
            mode: s.mode, weatherSensorId: s.weatherSensorId ?? '', applicationRateMmH: s.applicationRateMmH ?? '',
            triggerDeficitMm: s.triggerDeficitMm, maxRunMinutes: s.maxRunMinutes, rainEfficiency: s.rainEfficiency,
        });
        setShowSettings(true);
    };
    const saveSettings = () => run(async () => {
        await waterBalanceService.saveSettings(field.id, {
            ...form,
            weatherSensorId: form.weatherSensorId === '' ? null : Number(form.weatherSensorId),
            applicationRateMmH: form.applicationRateMmH === '' ? null : Number(form.applicationRateMmH),
            triggerDeficitMm: Number(form.triggerDeficitMm), maxRunMinutes: Number(form.maxRunMinutes), rainEfficiency: Number(form.rainEfficiency),
        });
        setShowSettings(false);
    });
    const reset = () => {
        if (!window.confirm(en ? 'Set the deficit to 0 now (e.g. after irrigating outside the system)?' : 'Su açığı şimdi 0 yapılsın mı (örn. sistem dışında sulama yaptıysanız)?')) return;
        run(() => waterBalanceService.reset(field.id));
    };
    const fromEmitters = () => {
        const v = emitterRateMmH({ ...emitter, areaM2: (field.area || 0) * 10000 });
        if (v) setForm(f => ({ ...f, applicationRateMmH: v }));
    };

    const notes = [...statusNotes(balance, lang), ...(balance.reason === 'no-weather-source' ? [en ? 'No weather source selected' : 'Hava verisi kaynağı seçilmedi'] : [])];
    const needed = balance.recommendation?.needed;
    const last = balance.lastIrrigation;

    return (
        <div style={box} data-testid="water-balance">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <Droplets size={13} color="#0369a1" />
                <strong style={{ color: '#0f172a' }}>{en ? 'Water balance' : 'Su bütçesi'}</strong>
                <span style={{ marginLeft: 'auto', background: s.mode === 'AUTO' ? '#dcfce7' : s.mode === 'OFF' ? '#f1f5f9' : '#e0f2fe', color: '#334155', borderRadius: 20, padding: '1px 8px', fontSize: 10, fontWeight: 700 }}>
                    {MODE_LABEL[lang][s.mode] ?? s.mode}
                </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: 20, fontWeight: 800, color: needed ? '#b45309' : '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                    {balance.deficitMm ?? '—'}
                </span>
                <span style={{ color: '#64748b' }}>mm {en ? 'deficit' : 'su açığı'}</span>
            </div>
            <div style={{ color: needed ? '#b45309' : '#475569', fontWeight: needed ? 700 : 500, margin: '2px 0 4px' }}>
                {recommendationText(balance, lang)}
            </div>
            {notes.map(n => <div key={n} style={{ color: '#b45309', fontSize: 11 }}>⚠ {n}</div>)}
            <div style={{ color: '#94a3b8', fontSize: 10.5 }}>
                {last ? `${en ? 'Last irrigation' : 'Son sulama'}: ${Math.round(last.mm * 10) / 10} mm · ${new Date(last.at).toLocaleString(en ? 'en-GB' : 'tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                      : (en ? 'No irrigation recorded' : 'Kayıtlı sulama yok')}
                {balance.start && ` · ${en ? 'since' : 'başlangıç'} ${new Date(balance.start).toLocaleDateString(en ? 'en-GB' : 'tr-TR')}`}
            </div>

            {s.mode !== 'OFF' && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
                    <input style={input} type="number" min="0" step="0.1" value={amount} onChange={e => setAmount(e.target.value)}
                        aria-label={en ? 'Irrigated amount' : 'Verilen su'} placeholder={unit} />
                    <select value={unit} onChange={e => setUnit(e.target.value)} style={{ ...input, width: 'auto' }}>
                        <option value="mm">mm</option>
                        <option value="min" disabled={!(s.applicationRateMmH > 0)}>{en ? 'min' : 'dk'}</option>
                    </select>
                    <button style={btn('#0369a1')} disabled={busy} onClick={submitApplied}>{en ? 'Irrigated' : 'Suladım'}</button>
                </div>
            )}

            {isAdmin && !showSettings && (
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <button style={btn('#e2e8f0', '#334155')} onClick={openSettings}>{en ? 'Settings' : 'Ayarlar'}</button>
                    <button style={btn('#e2e8f0', '#334155')} disabled={busy} onClick={reset}>{en ? 'Reset to 0' : 'Sıfırla'}</button>
                </div>
            )}

            {isAdmin && showSettings && form && (
                <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 8, paddingTop: 6 }}>
                    <label style={label}>{en ? 'Mode' : 'Mod'}
                        <select value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })} style={{ ...input, width: 110 }}>
                            {['OFF', 'ADVISE', 'AUTO'].map(m => <option key={m} value={m}>{MODE_LABEL[lang][m]}</option>)}
                        </select>
                    </label>
                    <label style={label}>{en ? 'Weather source' : 'Hava verisi kaynağı'}
                        <select value={form.weatherSensorId} onChange={e => setForm({ ...form, weatherSensorId: e.target.value })} style={{ ...input, width: 150 }}>
                            <option value="">—</option>
                            {weatherSensors(sensors).map(w => <option key={w.id} value={w.id}>{w.name.replace(/^Meteo - FieldClimate\s*/i, '')}</option>)}
                        </select>
                    </label>
                    <label style={label}>{en ? 'Application rate (mm/h)' : 'Sulama hızı (mm/saat)'}
                        <input style={input} type="number" min="0" step="0.1" value={form.applicationRateMmH} onChange={e => setForm({ ...form, applicationRateMmH: e.target.value })} />
                    </label>
                    <div style={{ ...label, color: '#64748b', fontSize: 11 }}>
                        {en ? 'or emitter L/h × count' : 'ya da damlatıcı L/sa × adet'}
                        <span style={{ display: 'flex', gap: 4 }}>
                            <input style={{ ...input, width: 48 }} type="number" value={emitter.litresPerHour} onChange={e => setEmitter({ ...emitter, litresPerHour: e.target.value })} aria-label="L/h" />
                            <input style={{ ...input, width: 64 }} type="number" value={emitter.emitters} onChange={e => setEmitter({ ...emitter, emitters: e.target.value })} aria-label={en ? 'emitters' : 'adet'} />
                            <button style={btn('#e2e8f0', '#334155')} onClick={fromEmitters}>=</button>
                        </span>
                    </div>
                    <label style={label}>{en ? 'Trigger (mm)' : 'Sulama eşiği (mm)'}
                        <input style={input} type="number" min="0" step="0.5" value={form.triggerDeficitMm} onChange={e => setForm({ ...form, triggerDeficitMm: e.target.value })} />
                    </label>
                    <label style={label}>{en ? 'Max run (min)' : 'En uzun sulama (dk)'}
                        <input style={input} type="number" min="1" step="5" value={form.maxRunMinutes} onChange={e => setForm({ ...form, maxRunMinutes: e.target.value })} />
                    </label>
                    <label style={label}>{en ? 'Rain efficiency (0–1)' : 'Yağış verimi (0–1)'}
                        <input style={input} type="number" min="0" max="1" step="0.05" value={form.rainEfficiency} onChange={e => setForm({ ...form, rainEfficiency: e.target.value })} />
                    </label>
                    {form.mode === 'AUTO' && (
                        <div style={{ color: '#b45309', fontSize: 11, margin: '4px 0' }}>
                            {en ? 'AUTO opens the field valve by itself. It needs a controllable valve, an application rate, Kc and fresh weather data.'
                                : 'Otomatik mod vanayı kendisi açar. Kontrol edilebilir bir vana, sulama hızı, Kc ve taze hava verisi gerekir.'}
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                        <button style={btn('#0369a1')} disabled={busy} onClick={saveSettings}>{en ? 'Save' : 'Kaydet'}</button>
                        <button style={btn('#e2e8f0', '#334155')} onClick={() => setShowSettings(false)}>{en ? 'Cancel' : 'Vazgeç'}</button>
                    </div>
                </div>
            )}
            {error && <div style={{ color: '#dc2626', fontSize: 11, marginTop: 4 }}>{error}</div>}
        </div>
    );
}
